import { useDebounce } from '@/app/hooks/useDebounce'
import { useGetAllUserLinks } from '@/app/hooks/useGetAllUserLinks'
import { selfHireService } from '@/app/services/selfHireService'
import { Links } from '@/app/services/selfHireService/getAllUserLinks'
import { useToast } from '@/view/components/ui/use-toast'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const useLinksController = () => {
  const [searchValue, setSearchValue] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedLink, setSelectedLink] = useState({ name: '' })
  const [isCreateLinkModalOpen, setIsCreateLinkModalOpen] = useState(false)
  const [newLinkName, setNewLinkName] = useState('')
  const [selectedTable, setSelectedTable] = useState('')
  const queryClient = useQueryClient()
  const debouncedSearchValue = useDebounce(500, searchValue)
  const [page, setPage] = useState(1)
  const limit = 10

  const { toast } = useToast()

  const navigate = useNavigate()

  const { data, isFetching, setParams } = useGetAllUserLinks()

  // Flatten pages from InfiniteData
  const flattenedData = data?.pages?.[0]
  const totalPages = Math.ceil((flattenedData?.pagination.total || 0) / limit)

  const setSearch = useCallback(() => {
    setParams((state) => ({
      ...state,
      search: debouncedSearchValue,
      skip: 0,
    }))
    setPage(1)
  }, [debouncedSearchValue, setParams])

  useEffect(() => {
    setSearch()
  }, [setSearch])

  useEffect(() => {
    setParams((prev) => ({
      ...prev,
      skip: (page - 1) * limit,
    }))
  }, [page, setParams])

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }

  const handlePrevious = () => {
    if (page > 1) setPage(page - 1)
  }

  const handleNext = () => {
    if (page < totalPages) setPage(page + 1)
  }

  const { mutate: createLinkMutation, isPending } = useMutation({
    mutationFn: async ({
      name,
      tableId,
    }: {
      name: string
      tableId: string
    }) => {
      return await selfHireService.createLink({ name, tableId })
    },
    onSuccess: (variables) => {
      queryClient.invalidateQueries({ queryKey: ['AllUserLinks'] })

      setIsCreateLinkModalOpen(false)

      setNewLinkName('')
      setSelectedTable('')

      setSelectedLink({ name: variables.name })
      setIsModalOpen(true)

      toast({
        variant: 'default',
        title: 'Link criado com sucesso!',
      })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title:
          error?.message || 'Não foi possível criar o link, tente novamente',
      })
    },
  })

  const handleOpenModal = (link: Links) => {
    setSelectedLink({ name: link.name })
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleCreateLink = () => {
    if (newLinkName && selectedTable) {
      createLinkMutation({
        name: newLinkName.trim(),
        tableId: selectedTable,
      })
    }
  }

  const handleLinkClick = (link: Links) => {
    if (flattenedData?.config.details) return navigate(`/links/${link._id}`)
    return handleOpenModal(link)
  }

  return {
    data: flattenedData,
    isFetching,
    handleSearchInputChange,
    searchValue,
    isModalOpen,
    selectedLink,
    isCreateLinkModalOpen,
    newLinkName,
    selectedTable,
    setIsCreateLinkModalOpen,
    setNewLinkName,
    setSelectedTable,
    handleOpenModal,
    handleCloseModal,
    handleCreateLink,
    isPending,
    handleLinkClick,
    page,
    totalPages,
    handleNext,
    handlePrevious,
  }
}

export default useLinksController
